import { useState, useRef } from "react";
import { useLocation } from "wouter";
import {
  Shield, Eye, EyeOff, Loader2, ChevronDown,
  Search, Settings2, LogOut as ExitIcon, ArrowRight,
  BadgeCheck, TrendingUp, Bell, FileText, BarChart3,
  Users, Zap, Lock, Globe, Layers, Star, X, CheckCircle2,
  Sun, Moon,
} from "lucide-react";
import { PortalSwitcher } from "@/components/portal-switcher";
import { useThemeMode } from "@/lib/use-theme-mode";
import { ForgotPasswordModal } from "@/pages/auth/forgot-password-modal";

const BASE = import.meta.env.BASE_URL;

export default function InvestorLogin() {
  const [, navigate] = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const loginRef = useRef<HTMLDivElement>(null);
  const { mode: themeMode, toggle: toggleTheme } = useThemeMode("investor", "dark");
  const ThemeIcon = themeMode === "dark" ? Sun : Moon;

  const [showInquiry, setShowInquiry] = useState(false);
  const [inquirySubmitting, setInquirySubmitting] = useState(false);
  const [inquirySuccess, setInquirySuccess] = useState(false);
  const [inquiryError, setInquiryError] = useState<string | null>(null);
  const [inquiryForm, setInquiryForm] = useState({
    name: "", email: "", phone: "", city: "", investmentRange: "", product: "", message: "",
  });

  const handleInquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInquirySubmitting(true);
    setInquiryError(null);
    try {
      const res = await fetch(`${BASE}api/investor-portal/inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(inquiryForm),
      });
      if (res.ok) {
        setInquirySuccess(true);
      } else {
        const err = await res.json();
        setInquiryError(err.error || "Something went wrong. Please try again.");
      }
    } catch {
      setInquiryError("Unable to connect. Please try again.");
    }
    setInquirySubmitting(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${BASE}api/investor-portal/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password }),
      });

      if (res.ok) {
        const data = await res.json();
        localStorage.setItem("investor_token", data.token);
        localStorage.setItem("investor_data", JSON.stringify(data.investor));
        // First-login or admin-reset path: bounce straight to change-password.
        if (data.mustChangePassword) {
          localStorage.setItem("investor_must_change_password", "true");
          navigate("/investor/change-password");
        } else {
          localStorage.removeItem("investor_must_change_password");
          navigate("/investor/portal");
        }
      } else {
        const err = await res.json();
        setError(err.error || "Login failed");
      }
    } catch {
      setError("Unable to connect. Please try again.");
    }
    setLoading(false);
  };

  const scrollToLogin = () => {
    loginRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const features = [
    { icon: BarChart3, title: "Live Portfolio Tracking", desc: "Real-time updates on all your portfolio companies, financials, and milestones." },
    { icon: Bell, title: "ROFR Notifications", desc: "Instant alerts when new funding rounds open — exercise your Right of First Refusal in one click." },
    { icon: FileText, title: "Document Vault", desc: "Secure access to shareholder agreements, cap tables, and investment memos." },
    { icon: TrendingUp, title: "AI-Powered Intel", desc: "Meera, our AI analyst, generates deep research and competitive intelligence on every company." },
    { icon: Globe, title: "Founder Showcase", desc: "Access Kal Ke Krorepati — Shubh Labh media profiles and ShubhLabh clips for each venture." },
    { icon: Zap, title: "Exit Support", desc: "After 3 years, leverage Favcy Advisors — our investment banking arm — for strategic exits." },
  ];

  return (
    <div data-investor-theme={themeMode} className="min-h-screen bg-[#0A0A0A] text-white overflow-y-auto">
      <button
        onClick={toggleTheme}
        title={`Switch to ${themeMode === "dark" ? "day" : "night"} mode`}
        aria-label="Toggle theme"
        data-testid="button-theme-toggle"
        className="fixed top-4 right-4 z-[60] p-2 rounded-md text-white/60 hover:text-white bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.1] backdrop-blur transition-colors"
      >
        <ThemeIcon className="w-4 h-4" />
      </button>
      <div className="fixed inset-0 pointer-events-none opacity-[0.07]" style={{
        backgroundImage: "radial-gradient(circle at 20% 30%, #FF5C00 0%, transparent 50%), radial-gradient(circle at 80% 70%, #FF5C00 0%, transparent 40%), radial-gradient(circle at 50% 50%, #FF5C00 0%, transparent 60%)"
      }} />

      <div className="relative">
        <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/[0.08]">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center">
                <span className="text-white font-bold text-lg font-serif">F</span>
              </div>
              <div>
                <p className="text-white font-serif font-bold text-lg leading-none">Favcy Select</p>
                <p className="text-white/50 text-[9px] font-mono uppercase tracking-[0.2em]">Investor Portal</p>
              </div>
            </div>
            <button
              onClick={scrollToLogin}
              className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Sign In
            </button>
          </div>
          <div className="border-t border-white/[0.06] bg-[#0A0A0A]/60">
            <div className="max-w-6xl mx-auto px-6 py-2">
              <PortalSwitcher current="investor" tone="dark" />
            </div>
          </div>
        </nav>

        <section className="max-w-6xl mx-auto px-6 pt-20 pb-24 text-center">
          <div className="inline-flex items-center gap-2 bg-white/[0.06] border border-white/15 rounded-full px-4 py-1.5 mb-8">
            <Star className="w-3.5 h-3.5 text-[#FF5C00]" />
            <span className="text-white/70 text-xs font-medium">India's First Venture Builder-Led Secondary Equity Platform</span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold tracking-tight leading-[1.1] max-w-4xl mx-auto">
            Invest in <span className="text-[#FF5C00]">Tomorrow's</span> Market Leaders, <span className="text-[#FF5C00]">Today</span>
          </h1>
          <p className="text-white/70 text-lg md:text-xl mt-6 max-w-2xl mx-auto leading-relaxed">
            Favcy Select gives you curated access to secondary equity in venture-built startups — de-risked, managed, and media amplified.
          </p>
          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={scrollToLogin}
              className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-8 py-3.5 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2"
            >
              Access Your Portfolio <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => { setShowInquiry(true); setInquirySuccess(false); setInquiryError(null); setInquiryForm({ name: "", email: "", phone: "", city: "", investmentRange: "", product: "", message: "" }); }}
              className="border border-white/20 hover:border-white/40 text-white/80 hover:text-white px-8 py-3.5 rounded-xl text-sm font-medium transition-all"
            >
              Become an Investor
            </button>
          </div>
          <button onClick={scrollToLogin} className="mt-16 animate-bounce text-white/30 hover:text-white/50 transition-colors">
            <ChevronDown className="w-6 h-6" />
          </button>
        </section>

        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center mb-14">
            <p className="text-[#FF5C00] text-xs font-semibold uppercase tracking-widest mb-3">Choose Your Path</p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold">Two Ways to Invest</h2>
            <p className="text-white/60 text-sm mt-3 max-w-lg mx-auto">Both products give you full access to the Favcy Select platform. Pick the one that matches your investment style.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="relative bg-white/[0.04] border border-purple-500/25 rounded-2xl p-8 hover:bg-white/[0.06] transition-colors group">
              <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r from-purple-500 to-purple-400" />
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-full bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-white font-serif font-bold text-xl">Shubh Labh Pool</h3>
                  <p className="text-purple-400 text-xs font-medium">Diversified Portfolio</p>
                </div>
              </div>
              <div className="space-y-3.5 mb-6">
                <div className="flex items-start gap-2.5">
                  <BadgeCheck className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  <p className="text-white/70 text-sm">Minimum ticket: <span className="text-white font-medium">&#8377;6,00,000</span></p>
                </div>
                <div className="flex items-start gap-2.5">
                  <BadgeCheck className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  <p className="text-white/70 text-sm">Favcy allocates across multiple ventures for maximum diversification</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <BadgeCheck className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  <p className="text-white/70 text-sm">Platform management: <span className="text-white font-medium">Free for 2 years</span>, then &#8377;14,999/yr</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <BadgeCheck className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                  <p className="text-white/70 text-sm">Ideal for first-time startup investors seeking hands-off exposure</p>
                </div>
              </div>
              <div className="bg-purple-500/10 rounded-lg px-4 py-2.5 text-center">
                <p className="text-purple-300 text-[11px]">Trust Favcy's venture-building expertise to build your portfolio</p>
              </div>
            </div>

            <div className="relative bg-white/[0.04] border border-cyan-500/25 rounded-2xl p-8 hover:bg-white/[0.06] transition-colors group">
              <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl bg-gradient-to-r from-cyan-500 to-cyan-400" />
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
                  <Search className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-white font-serif font-bold text-xl">Pre-Screening</h3>
                  <p className="text-cyan-400 text-xs font-medium">Curated Selection</p>
                </div>
              </div>
              <div className="space-y-3.5 mb-6">
                <div className="flex items-start gap-2.5">
                  <BadgeCheck className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                  <p className="text-white/70 text-sm">Minimum ticket: <span className="text-white font-medium">&#8377;12,00,000</span></p>
                </div>
                <div className="flex items-start gap-2.5">
                  <BadgeCheck className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                  <p className="text-white/70 text-sm">Preview each company before allocation — full visibility into the portfolio</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <BadgeCheck className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                  <p className="text-white/70 text-sm">Platform management: <span className="text-white font-medium">Free for 1 year</span>, then &#8377;14,999/yr</p>
                </div>
                <div className="flex items-start gap-2.5">
                  <BadgeCheck className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                  <p className="text-white/70 text-sm">For experienced investors who want visibility before allocation</p>
                </div>
              </div>
              <div className="bg-cyan-500/10 rounded-lg px-4 py-2.5 text-center">
                <p className="text-cyan-300 text-[11px]">Available after the media show is shot — review ventures before Favcy allocates</p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-white/[0.08] bg-white/[0.02]">
          <div className="max-w-5xl mx-auto px-6 py-20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-[#FF5C00] text-xs font-semibold uppercase tracking-widest mb-3">The Favcy Edge</p>
                <h2 className="text-3xl md:text-4xl font-serif font-bold leading-tight">
                  Venture Building.<br />
                  Media <span className="text-[#FF5C00]">Amplification</span>.<br />
                  Secondary Equity.
                </h2>
                <p className="text-white/65 text-sm mt-5 leading-relaxed max-w-md">
                  Favcy doesn't just build ventures — it accelerates them through national media. Every portfolio company is featured on <span className="text-white/90 font-medium">Kal Ke Krorepati — Shubh Labh</span>, broadcast to millions across India.
                </p>
                <p className="text-white/65 text-sm mt-3 leading-relaxed max-w-md">
                  Favcy Select is where this comes full circle — investors acquire secondary equity in media-amplified, venture-built companies with real traction and national visibility.
                </p>
              </div>
              <div className="space-y-4">
                <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-400 font-serif font-bold text-sm">1</span>
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Favcy Builds</p>
                    <p className="text-white/55 text-xs mt-0.5">Ventures are co-built with founders through Favcy's venture studio model</p>
                  </div>
                </div>
                <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/15 border border-purple-500/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-purple-400 font-serif font-bold text-sm">2</span>
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Media Amplifies</p>
                    <p className="text-white/55 text-xs mt-0.5">Kal Ke Krorepati — Shubh Labh showcases ventures to a national audience</p>
                  </div>
                </div>
                <div className="bg-white/[0.04] border border-[#FF5C00]/20 rounded-xl p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#FF5C00]/15 border border-[#FF5C00]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#FF5C00] font-serif font-bold text-sm">3</span>
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">Select Invests</p>
                    <p className="text-white/55 text-xs mt-0.5">Investors acquire secondary equity in companies that are built, proven, and visible</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-white/[0.08]">
          <div className="max-w-6xl mx-auto px-6 py-20">
            <div className="text-center mb-14">
              <p className="text-[#FF5C00] text-xs font-semibold uppercase tracking-widest mb-3">The Journey</p>
              <h2 className="text-3xl md:text-4xl font-serif font-bold">How Favcy Select Works</h2>
              <p className="text-white/60 text-sm mt-3 max-w-lg mx-auto">A structured three-stage process from investment to exit, managed entirely through the Favcy Select platform.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <div className="relative bg-white/[0.04] border border-white/[0.12] rounded-2xl p-7">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/25 flex items-center justify-center">
                    <Search className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-emerald-400 text-[10px] uppercase tracking-wider font-semibold">Stage 1</p>
                    <p className="text-white font-semibold">Origination</p>
                  </div>
                </div>
                <p className="text-white/65 text-sm leading-relaxed">
                  Favcy sources, vets, and structures the deal — due diligence, valuation, legal, all done for you before you invest.
                </p>
                <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#0A0A0A] border border-white/15 items-center justify-center z-10">
                  <ArrowRight className="w-3 h-3 text-white/40" />
                </div>
              </div>

              <div className="relative bg-white/[0.04] border border-white/[0.12] rounded-2xl p-7">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-500/20 border border-blue-500/25 flex items-center justify-center">
                    <Settings2 className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-blue-400 text-[10px] uppercase tracking-wider font-semibold">Stage 2</p>
                    <p className="text-white font-semibold">Management</p>
                  </div>
                </div>
                <p className="text-white/65 text-sm leading-relaxed">
                  Track your portfolio on the <span className="text-white/90">Favcy Select platform</span> — live updates, AI analysis, ROFR alerts, and venture support. Free for <span className="text-white/90">2 years (Shubh Labh Pool)</span> or <span className="text-white/90">1 year (Pre-Screening)</span>, then &#8377;14,999/yr.
                </p>
                <div className="hidden md:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#0A0A0A] border border-white/15 items-center justify-center z-10">
                  <ArrowRight className="w-3 h-3 text-white/40" />
                </div>
              </div>

              <div className="bg-white/[0.04] border border-white/[0.12] rounded-2xl p-7">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#FF5C00]/20 border border-[#FF5C00]/25 flex items-center justify-center">
                    <ExitIcon className="w-5 h-5 text-[#FF5C00]" />
                  </div>
                  <div>
                    <p className="text-[#FF5C00] text-[10px] uppercase tracking-wider font-semibold">Stage 3</p>
                    <p className="text-white font-semibold">Exit</p>
                  </div>
                </div>
                <p className="text-white/65 text-sm leading-relaxed">
                  After 3 years, Favcy Advisors — our in-house investment banking arm — facilitates your exit through secondaries, buybacks, or strategic sales at a <span className="text-white/90">5% success fee</span>.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center mb-14">
            <p className="text-[#FF5C00] text-xs font-semibold uppercase tracking-widest mb-3">Platform Features</p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold">Everything You Need, In One Place</h2>
            <p className="text-white/60 text-sm mt-3 max-w-lg mx-auto">The Favcy Select platform is your command centre for every investment.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {features.map((f, i) => (
              <div key={i} className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-6 hover:bg-white/[0.06] hover:border-white/15 transition-all group">
                <div className="w-10 h-10 rounded-lg bg-[#FF5C00]/15 border border-[#FF5C00]/20 flex items-center justify-center mb-4 group-hover:bg-[#FF5C00]/20 transition-colors">
                  <f.icon className="w-5 h-5 text-[#FF5C00]" />
                </div>
                <h3 className="text-white font-semibold text-sm mb-1.5">{f.title}</h3>
                <p className="text-white/60 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-6 pb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { value: "50+", label: "Portfolio Companies" },
              { value: "₹100Cr+", label: "Capital Deployed" },
              { value: "8+", label: "Successful Exits" },
              { value: "300+", label: "Select Investors" },
            ].map((stat, i) => (
              <div key={i} className="text-center py-4">
                <p className="text-2xl md:text-3xl font-bold font-mono text-[#FF5C00]">{stat.value}</p>
                <p className="text-white/50 text-xs mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section ref={loginRef} className="border-t border-white/[0.08] bg-white/[0.02]">
          <div className="max-w-md mx-auto px-6 py-20">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF5C00] to-[#E55200] mb-4">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-serif font-bold">Sign In to Your Portal</h2>
              <p className="text-white/60 text-sm mt-2">Access your Favcy Select investor dashboard</p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white/[0.04] border border-white/[0.12] rounded-2xl p-8 space-y-5">
              {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm text-center">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-white/70 text-xs uppercase tracking-wider mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50 focus:ring-1 focus:ring-[#FF5C00]/20 transition-colors"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label className="block text-white/70 text-xs uppercase tracking-wider mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-4 py-3 pr-10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50 focus:ring-1 focus:ring-[#FF5C00]/20 transition-colors"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <div className="mt-2 text-right">
                  <button
                    type="button"
                    onClick={() => setForgotOpen(true)}
                    className="text-[11px] text-white/40 hover:text-[#FF5C00] transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !email || !password}
                className="w-full bg-gradient-to-r from-[#FF5C00] to-[#E55200] text-white py-3 rounded-lg font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>

            <div className="mt-6 flex items-center justify-center gap-2 text-white/40 text-xs">
              <Shield className="w-3 h-3" />
              <span>Secured by Favcy Venture Builders Private Limited</span>
            </div>
          </div>
        </section>

        <footer className="border-t border-white/[0.08] bg-[#0A0A0A]">
          <div className="max-w-6xl mx-auto px-6 py-10">
            <div className="text-[11px] text-white/45 leading-relaxed space-y-3 max-w-3xl">
              <p>
                Favcy Select involves the purchase of secondary equity in early-stage companies. Investing in startups involves a high degree of risk, including the potential loss of your entire investment. Past performance is not indicative of future results.
              </p>
              <p>
                The companies featured on "Kal Ke Krorepati — Shubh Labh" receive media exposure as part of Favcy's venture building program; such coverage does not constitute an endorsement or guarantee of any company's future performance. The Right of First Refusal (ROFR) is subject to the terms of the Favcy Select agreement.
              </p>
              <p>
                Management is delivered via the Favcy Select platform — free for 2 years (Shubh Labh Pool) or 1 year (Pre-Screening), then &#8377;14,999/year. Exit facilitation is available after 3 years at a 5% success fee via Favcy Advisors, our in-house investment banking arm.
              </p>
            </div>
            <div className="mt-8 pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-[11px] text-white/35">&copy; {new Date().getFullYear()} Favcy Venture Builders Private Limited. All rights reserved.</p>
              <a href="mailto:Kavya@favcyselect.com" className="text-[11px] text-[#FF5C00]/70 hover:text-[#FF5C00] transition-colors">Kavya@favcyselect.com</a>
            </div>
            <div className="mt-6 pt-6 border-t border-white/[0.06]">
              <PortalSwitcher current="investor" tone="dark" />
            </div>
          </div>
        </footer>
      </div>

      {showInquiry && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowInquiry(false)} />
          <div className="relative bg-[#111111] border border-white/15 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-[#111111] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h3 className="text-white font-serif font-bold text-lg">Become an Investor</h3>
                <p className="text-white/50 text-xs mt-0.5">Tell us about yourself and we'll reach out</p>
              </div>
              <button onClick={() => setShowInquiry(false)} className="text-white/40 hover:text-white/70 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            {inquirySuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                </div>
                <h4 className="text-white font-serif font-bold text-xl mb-2">Thank You!</h4>
                <p className="text-white/60 text-sm leading-relaxed max-w-sm mx-auto">
                  Your inquiry has been received. Our team will reach out to you shortly to discuss the Favcy Select investment opportunity.
                </p>
                <button
                  onClick={() => setShowInquiry(false)}
                  className="mt-6 bg-[#FF5C00] hover:bg-[#E55200] text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleInquirySubmit} className="p-6 space-y-4">
                {inquiryError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm text-center">
                    {inquiryError}
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/70 text-xs uppercase tracking-wider mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      value={inquiryForm.name}
                      onChange={(e) => setInquiryForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50 focus:ring-1 focus:ring-[#FF5C00]/20 transition-colors"
                      placeholder="Rahul Sharma"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-xs uppercase tracking-wider mb-1.5">Email *</label>
                    <input
                      type="email"
                      value={inquiryForm.email}
                      onChange={(e) => setInquiryForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50 focus:ring-1 focus:ring-[#FF5C00]/20 transition-colors"
                      placeholder="rahul@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/70 text-xs uppercase tracking-wider mb-1.5">Phone</label>
                    <input
                      type="tel"
                      value={inquiryForm.phone}
                      onChange={(e) => setInquiryForm(f => ({ ...f, phone: e.target.value }))}
                      className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50 focus:ring-1 focus:ring-[#FF5C00]/20 transition-colors"
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div>
                    <label className="block text-white/70 text-xs uppercase tracking-wider mb-1.5">City</label>
                    <input
                      type="text"
                      value={inquiryForm.city}
                      onChange={(e) => setInquiryForm(f => ({ ...f, city: e.target.value }))}
                      className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50 focus:ring-1 focus:ring-[#FF5C00]/20 transition-colors"
                      placeholder="Mumbai"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-white/70 text-xs uppercase tracking-wider mb-1.5">Investment Range</label>
                    <select
                      value={inquiryForm.investmentRange}
                      onChange={(e) => setInquiryForm(f => ({ ...f, investmentRange: e.target.value }))}
                      className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF5C00]/50 focus:ring-1 focus:ring-[#FF5C00]/20 transition-colors appearance-none"
                    >
                      <option value="" className="bg-[#111]">Select range</option>
                      <option value="₹6-12 Lakhs" className="bg-[#111]">₹6–12 Lakhs</option>
                      <option value="₹12-25 Lakhs" className="bg-[#111]">₹12–25 Lakhs</option>
                      <option value="₹25-50 Lakhs" className="bg-[#111]">₹25–50 Lakhs</option>
                      <option value="₹50 Lakhs - 1 Crore" className="bg-[#111]">₹50 Lakhs – 1 Crore</option>
                      <option value="₹1 Crore+" className="bg-[#111]">₹1 Crore+</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-white/70 text-xs uppercase tracking-wider mb-1.5">Interested In</label>
                    <select
                      value={inquiryForm.product}
                      onChange={(e) => setInquiryForm(f => ({ ...f, product: e.target.value }))}
                      className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#FF5C00]/50 focus:ring-1 focus:ring-[#FF5C00]/20 transition-colors appearance-none"
                    >
                      <option value="" className="bg-[#111]">Select product</option>
                      <option value="Shubh Labh Pool" className="bg-[#111]">Shubh Labh Pool (₹6L minimum)</option>
                      <option value="Pre-Screening" className="bg-[#111]">Pre-Screening (₹12L minimum)</option>
                      <option value="Not sure yet" className="bg-[#111]">Not sure yet</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-white/70 text-xs uppercase tracking-wider mb-1.5">Message (optional)</label>
                  <textarea
                    value={inquiryForm.message}
                    onChange={(e) => setInquiryForm(f => ({ ...f, message: e.target.value }))}
                    className="w-full bg-white/[0.06] border border-white/15 rounded-lg px-4 py-2.5 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#FF5C00]/50 focus:ring-1 focus:ring-[#FF5C00]/20 transition-colors resize-none"
                    rows={3}
                    placeholder="Any questions or comments..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={inquirySubmitting || !inquiryForm.name || !inquiryForm.email}
                  className="w-full bg-gradient-to-r from-[#FF5C00] to-[#E55200] text-white py-3 rounded-lg font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {inquirySubmitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                  ) : (
                    "Submit Interest"
                  )}
                </button>

                <p className="text-white/30 text-[11px] text-center leading-relaxed">
                  Our team will contact you within 24 hours. Your information is kept confidential and will only be used to discuss Favcy Select.
                </p>
              </form>
            )}
          </div>
        </div>
      )}

      <ForgotPasswordModal audience="investor" open={forgotOpen} initialEmail={email} onClose={() => setForgotOpen(false)} />
    </div>
  );
}
